<template>
    <div class="search-page">
        <div class="search-header">
            <div class="search-input-container">
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="form-control"
                    :placeholder="t.placeholder"
                    @input="handleSearch"
                    @keydown.down.prevent="selectNextResult"
                    @keydown.up.prevent="selectPreviousResult"
                    @keydown.enter="handleEnterKey"
                >
                <div class="search-icon" :class="{ 'loading': isLoading }">
                    <i class="fa-regular fa-magnifying-glass"></i>
                </div>
                <button v-if="searchQuery" @click="clearSearch" class="close-button">
                    <i class="fa-regular fa-times"></i>
                </button>
            </div>
        </div>

        <div class="search-filters">
            <button
                class="filter-button"
                :class="{'active': activeFilter === 'all'}"
                @click="setFilter('all')"
            >
                {{ t.all }} ({{ totalResults }})
            </button>
            <button
                v-for="category in categories"
                :key="category.name"
                class="filter-button"
                :class="{'active': activeFilter === category.key}"
                @click="setFilter(category.key)"
            >
                {{ getCategoryName(category.originalKey, category.count > 1) }} ({{ category.count }})
            </button>
        </div>

        <div class="search-results">
            <div v-if="isLoading" class="loading-spinner py-5">
                <div class="spinner"></div>
                <p>{{ t.loading }}</p>
            </div>

            <div v-else-if="filteredResults.length > 0" class="results-container">
                <div class="results-header mb-3">
                    <h2>{{ displayResultCount }}</h2>
                </div>

                <div class="result-list">
                    <div v-for="(result, index) in paginatedResults"
                         :key="`${result.id}-${index}`"
                         class="result-item"
                         :class="{'selected': selectedIndex === index, [type(result)]: true}"
                         @mouseover="selectedIndex = index">
                        <SearchResults :result="result" :current-language="currentLanguage"/>
                    </div>
                </div>

                <div v-if="totalPages > 1" class="pagination-section mt-4">
                    <nav aria-label="Search results pagination">
                        <ul class="pagination justify-content-center">
                            <li class="page-item" :class="{ 'disabled': currentPage === 1 }">
                                <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
                                    <i class="fa-regular fa-chevron-left"></i>
                                </button>
                            </li>

                            <template v-for="page in paginationNumbers" :key="page">
                                <li v-if="page !== '...'" class="page-item" :class="{ 'active': currentPage === page }">
                                    <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                                </li>
                                <li v-else class="page-item disabled">
                                    <span class="page-link">{{ page }}</span>
                                </li>
                            </template>

                            <li class="page-item" :class="{ 'disabled': currentPage === totalPages }">
                                <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
                                    <i class="fa-regular fa-chevron-right"></i>
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <div v-else-if="searchQuery.length >= 2" class="no-results">
                <div class="text-center py-5">
                    <div class="no-results-icon mb-3">
                        <i class="fa-regular fa-search fa-3x text-muted"></i>
                    </div>
                    <h3>{{ t.no_results_title }}</h3>
                    <p class="text-muted">{{ t.no_results_text.replace(':query', searchQuery) }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {useDebounceFn} from '@vueuse/core';
import axios from 'axios';
import SearchResults from '../components/SearchResults.vue';

const props = defineProps({
    initialQuery: {
        type:    String,
        default: ''
    }
});

const searchQuery    = ref(props.initialQuery || '');
const searchResults  = ref([]);
const isLoading      = ref(false);
const totalResults   = ref(0);
const searchInput    = ref(null);
const currentPage    = ref(1);
const resultsPerPage = 10;
const activeFilter   = ref('all');
const selectedIndex  = ref(-1);
const translations   = ref({});

// Get current language from the URL or default to 'nl'
const currentLanguage = computed(() => {
    const path     = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    return (segments.length > 0 && ['nl', 'en'].includes(segments[0])) ? segments[0] : 'nl';
});

// Translation helper
const t = computed(() => translations.value.search || {
    placeholder:      'Zoeken...',
    all:              'Alles',
    no_results_title: 'Geen resultaten gevonden',
    no_results_text:  'Geen resultaten gevonden voor ":query"',
    result:           'resultaat',
    results:          'resultaten',
    for:              'voor',
    loading:          'Zoeken...',
    close:            'Sluiten',
    search:           'Zoeken',
    categories:       {}
});

// Load translations
const loadTranslations = async() => {
    try {
        const {data}       = await axios.get('/api/search/translations', {
            params: {locale: currentLanguage.value}
        });
        translations.value = data;
    } catch(error) {
        console.error('Failed to load translations:', error);
    }
};

// Helper to get category name with pluralization
const getCategoryName = (categoryKey, isPlural = false) => {
    const categoryTranslation = t.value.categories[categoryKey];
    if(!categoryTranslation) return categoryKey;

    const [singular, plural] = categoryTranslation.split('|');
    return isPlural ? (plural || singular) : singular;
};

// Handle search with debounce
const handleSearch = useDebounceFn(async() => {
    if(searchQuery.value.length < 2) {
        searchResults.value = [];
        totalResults.value  = 0;
        return;
    }

    isLoading.value     = true;
    currentPage.value   = 1;
    selectedIndex.value = -1;

    try {
        const {data} = await axios.get('/api/search', {
            params: {
                query: searchQuery.value,
                lang:  currentLanguage.value
            }
        });

        searchResults.value = data.hits || [];
        totalResults.value  = data.estimatedTotalHits || data.hits.length;

        // Update URL with search query
        const url = new URL(window.location);
        url.searchParams.set('q', searchQuery.value);
        window.history.pushState({}, '', url);

    } catch(error) {
        console.error('Search error:', error);
        searchResults.value = [];
        totalResults.value  = 0;
    } finally {
        isLoading.value = false;
    }
}, 300);

// Clear search
const clearSearch = () => {
    searchQuery.value   = '';
    searchResults.value = [];
    totalResults.value  = 0;
    selectedIndex.value = -1;

    // Update URL
    const url = new URL(window.location);
    url.searchParams.delete('q');
    window.history.pushState({}, '', url);
};

// Categories computed
const categories = computed(() => {
    const categoryCounts = {};

    searchResults.value.forEach(result => {
        const category = result.index_singular;
        if(typeof category === 'undefined') {
            console.log(result);
            return;
        }
        if(!categoryCounts[category]) {
            categoryCounts[category] = {
                key:         category.toLowerCase().replace(/\s+/g, '_'),
                originalKey: category.toLowerCase(),
                name:        category,
                namePlural:  result.index_plural,
                count:       0
            };
        }
        categoryCounts[category].count++;
    });

    return Object.values(categoryCounts).sort((a, b) => b.count - a.count);
});

// Filtered results based on active category
const filteredResults = computed(() => {
    if(activeFilter.value === 'all') {
        return searchResults.value;
    }

    return searchResults.value.filter(result => {
        const resultCategory = result.index_singular.toLowerCase().replace(/\s+/g, '_');
        return resultCategory === activeFilter.value;
    });
});

// Paginated results
const paginatedResults = computed(() => {
    const startIndex = (currentPage.value - 1) * resultsPerPage;
    const endIndex   = startIndex + resultsPerPage;
    return filteredResults.value.slice(startIndex, endIndex);
});

// Total pages
const totalPages = computed(() => {
    return Math.ceil(filteredResults.value.length / resultsPerPage);
});

// Keyboard navigation functions
const selectNextResult = () => {
    if(paginatedResults.value.length > 0) {
        if(selectedIndex.value < paginatedResults.value.length - 1) {
            selectedIndex.value++;
        } else {
            selectedIndex.value = 0; // Loop back to first result
        }
    }
};

const selectPreviousResult = () => {
    if(paginatedResults.value.length > 0) {
        if(selectedIndex.value > 0) {
            selectedIndex.value--;
        } else {
            selectedIndex.value = paginatedResults.value.length - 1; // Loop to last result
        }
    }
};

const handleEnterKey = () => {
    if(selectedIndex.value >= 0 && selectedIndex.value < paginatedResults.value.length) {
        // Navigate to the selected result's URL
        const selectedResult = paginatedResults.value[selectedIndex.value];
        // For product variations, use product_url instead of url
        const targetUrl      = selectedResult.product_url || selectedResult.url;
        if(targetUrl) {
            window.location.href = targetUrl;
        }
    }
};

// Pagination display helpers
const paginationNumbers = computed(() => {
    if(totalPages.value <= 7) {
        return Array.from({length: totalPages.value}, (_, i) => i + 1);
    }

    if(currentPage.value <= 3) {
        return [1, 2, 3, 4, 5, '...', totalPages.value];
    }

    if(currentPage.value >= totalPages.value - 2) {
        return [1, '...', totalPages.value - 4, totalPages.value - 3, totalPages.value - 2, totalPages.value - 1, totalPages.value];
    }

    return [
        1,
        '...',
        currentPage.value - 1,
        currentPage.value,
        currentPage.value + 1,
        '...',
        totalPages.value
    ];
});

// Display text for result count
const displayResultCount = computed(() => {
    const filteredCount = filteredResults.value.length;

    if(filteredCount === 0) {
        return t.value.no_results_title;
    }

    const resultOrResults = filteredCount === 1 ? t.value.result : t.value.results;

    if(activeFilter.value === 'all') {
        return `${filteredCount} ${resultOrResults} ${t.value.for} "${searchQuery.value}"`;
    } else {
        const categoryName = getCategoryName(categories.value.find(c => c.key === activeFilter.value)?.originalKey || '');
        return `${filteredCount} ${categoryName} ${resultOrResults} ${t.value.for} "${searchQuery.value}"`;
    }
});

// Change category filter
const setFilter = (filter) => {
    activeFilter.value  = filter;
    currentPage.value   = 1; // Reset to first page when filter changes
    selectedIndex.value = -1; // Reset selected index
};

// Pagination navigation
const goToPage = (page) => {
    currentPage.value   = page;
    selectedIndex.value = -1; // Reset selected index when changing page

    // Get the scroll top to the search bar. Take off the navigation height.
    const searchHeaderOffset = document.querySelector('.search-header').getBoundingClientRect().top + window.scrollY - document.querySelector('nav.navbar.navbar-expand-xl').offsetHeight;
    window.scrollTo({top: searchHeaderOffset, behavior: 'smooth'});
};

// Initial search on mount if query provided
onMounted(() => {
    loadTranslations();
    if(searchQuery.value) {
        handleSearch();
    }
    searchInput.value?.focus();
});

// Reset pagination when filtered results change
watch(filteredResults, () => {
    if(currentPage.value > totalPages.value && totalPages.value > 0) {
        currentPage.value = totalPages.value;
    }
    selectedIndex.value = -1; // Reset selected index when results change
});

// Reset selected index when page changes
watch(currentPage, () => {
    selectedIndex.value = -1;
});

// Helper to determine result type class
const type = (result) => {
    const singularKey = result.index_singular?.toLowerCase();

    switch(singularKey) {
        case 'medewerker':
        case 'employee':
            return 'employee-result'
        case 'nieuwsbericht':
        case 'headline':
        case 'news':
        case 'artikel':
            return 'headline-result'
        case 'oplossing':
        case 'solution':
            return 'solution-result'
        case 'product':
            return 'product-result'
        case 'productgroep':
        case 'product group':
            return 'product-group-result'
        case 'productvariatie':
        case 'product variation':
            return 'product-variation-result'
        case 'accessoire':
        case 'accessory':
            return 'product-accessory-result'
        case 'concept':
            return 'concept-result'
        case 'vacature':
        case 'vacancy':
            return 'vacancy-result'
        case 'stap':
        case 'step':
            return 'total-approach-step-result'
        case 'pagina':
        case 'page':
            return 'page-result'
        case 'klantverhaal':
            return 'testimonial-result'
        default:
            return 'default-result'
    }
};
</script>